import React, { useState, useEffect, useMemo } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Vote, User, Upload, X, ArrowLeft, TrendingUp, TrendingDown, Shield, Loader2, LayoutGrid, List, Search } from 'lucide-react';

function VotingPage() {
  const [artists, setArtists] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [showBuyVotes, setShowBuyVotes] = useState(false);
  const [showNoVotesModal, setShowNoVotesModal] = useState(false);
  const [buyAmount, setBuyAmount] = useState(20);
  const [proofFile, setProofFile] = useState(null);
  const [leaderboard, setLeaderboard] = useState({});
  const [userVotes, setUserVotes] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  
  // Admin vote state
  const [showAdminVote, setShowAdminVote] = useState(null); // artist_id or null
  const [adminVoteAction, setAdminVoteAction] = useState('increase');
  const [adminVoteCount, setAdminVoteCount] = useState(10);
  const [adminVoteReason, setAdminVoteReason] = useState('');
  const [isSubmittingAdminVote, setIsSubmittingAdminVote] = useState(false);
  
  const navigate = useNavigate();

  // Filter and sort artists based on search query and votes
  const filteredArtists = useMemo(() => {
    let result = artists;
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(artist => 
        (artist.stage_name && artist.stage_name.toLowerCase().includes(query)) ||
        (artist.name && artist.name.toLowerCase().includes(query)) ||
        (artist.talents && artist.talents.some(t => t.toLowerCase().includes(query)))
      );
    }
    
    // Sort by votes (highest to lowest)
    result = [...result].sort((a, b) => {
      const votesA = leaderboard[a.user_id] || 0;
      const votesB = leaderboard[b.user_id] || 0;
      return votesB - votesA;
    });
    
    return result;
  }, [artists, searchQuery, leaderboard]);

  useEffect(() => {
    fetchCurrentUser();
    fetchArtists();
    fetchLeaderboard();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: 'include' });
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error('Not authenticated');
      }
      
      setCurrentUser(data);
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchArtists = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/users?is_artist=true`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error('Failed to fetch artists');
      }
      
      setArtists(data);
    } catch (error) {
      toast.error('Failed to load artists');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminVoteSubmit = async (artistId) => {
    if (!adminVoteReason.trim()) {
      toast.error('Please provide a reason for the vote adjustment');
      return;
    }
    if (adminVoteCount <= 0) {
      toast.error('Vote count must be greater than 0');
      return;
    }

    setIsSubmittingAdminVote(true);
    try {
      const response = await fetch(`${API}/admin/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          artist_id: artistId,
          vote_count: adminVoteCount,
          action: adminVoteAction,
          reason: adminVoteReason
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Votes ${adminVoteAction === 'increase' ? 'increased' : 'decreased'} successfully!`);
        setShowAdminVote(null);
        setAdminVoteReason('');
        setAdminVoteCount(10);
        fetchLeaderboard(); // Refresh the leaderboard
      } else {
        toast.error(data.detail || 'Failed to submit admin vote');
      }
    } catch (error) {
      toast.error('Failed to submit admin vote');
    } finally {
      setIsSubmittingAdminVote(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API}/votes/leaderboard`);
      const data = await response.json();
      
      const voteMap = {};
      data.forEach(entry => {
        voteMap[entry.artist_id] = entry.total_votes || 0;
      });
      setLeaderboard(voteMap);
    } catch (error) {
      console.error('Failed to fetch leaderboard');
    }
  };

  const handleVote = async (artistId) => {
    // Check if user has votes (skip for admins)
    const isAdmin = currentUser?.is_admin;
    if (!isAdmin && (!currentUser || currentUser.free_votes_remaining < 1)) {
      setShowNoVotesModal(true);
      return;
    }

    try {
      const response = await fetch(`${API}/votes?artist_id=${artistId}&event_id=default_event&vote_count=1`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.detail?.includes('Not enough')) {
          setShowNoVotesModal(true);
        } else if (data.detail?.includes('Judges can only vote')) {
          toast.error(data.detail);
        } else {
          throw new Error(data.detail || 'Failed to cast vote');
        }
        return;
      }

      // Update vote count in real-time
      setLeaderboard(prev => ({
        ...prev,
        [artistId]: (prev[artistId] || 0) + 1
      }));

      // Track user's vote for this artist
      setUserVotes(prev => ({
        ...prev,
        [artistId]: (prev[artistId] || 0) + 1
      }));

      // Update user's remaining votes locally (only for non-admins)
      if (!isAdmin) {
        setCurrentUser(prev => ({
          ...prev,
          free_votes_remaining: prev.free_votes_remaining - 1
        }));
      }

      toast.success('Vote cast successfully!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUnvote = async (artistId) => {
    if (!userVotes[artistId] || userVotes[artistId] < 1) {
      toast.error('You haven\'t voted for this artist');
      return;
    }

    try {
      const response = await fetch(`${API}/votes/unvote?artist_id=${artistId}&event_id=default_event`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to remove vote');
      }

      // Update vote count in real-time
      setLeaderboard(prev => ({
        ...prev,
        [artistId]: Math.max((prev[artistId] || 0) - 1, 0)
      }));

      // Update user's tracked votes
      setUserVotes(prev => ({
        ...prev,
        [artistId]: Math.max((prev[artistId] || 0) - 1, 0)
      }));

      // Return vote to user
      setCurrentUser(prev => ({
        ...prev,
        free_votes_remaining: prev.free_votes_remaining + 1
      }));

      toast.success('Vote reclaimed!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBuyVotes = async () => {
    if (!proofFile) {
      toast.error('Please upload proof of payment');
      return;
    }

    try {
      const proofUrl = 'https://example.com/proof/' + Date.now();
      
      const response = await fetch(`${API}/votes/buy?amount=${buyAmount}&proof_url=${encodeURIComponent(proofUrl)}`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit payment');
      }

      toast.success('Payment submitted for approval!');
      setShowBuyVotes(false);
      setShowNoVotesModal(false);
      setProofFile(null);
    } catch (error) {
      toast.error(error.message || 'Failed to submit payment');
    }
  };

  const voteOptions = [20, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000];

  return (
    <div className="min-h-screen pb-24">
      {/* No Votes Modal */}
      {showNoVotesModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="glass-panel p-8 max-w-md w-full text-center">
            <div className="mb-6">
              <Vote className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-2xl font-bold mb-2">Sorry, Your Free Votes are Finished</h2>
              <p className="text-muted-foreground">Purchase more votes to continue supporting your favorite artists!</p>
            </div>
            <div className="flex gap-4">
              <Button
                onClick={() => {
                  setShowNoVotesModal(false);
                  setShowBuyVotes(true);
                }}
                className="flex-1 bg-secondary text-black hover:brightness-110 rounded-full"
              >
                <DollarSign className="mr-2 h-5 w-5" />
                Buy More Votes
              </Button>
              <Button
                onClick={() => setShowNoVotesModal(false)}
                variant="outline"
                className="flex-1 rounded-full"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Back
              </Button>
            </div>
          </Card>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-start mb-4">
          <Button variant="ghost" onClick={() => navigate('/')}>
            ← Back to Home
          </Button>
          
          {/* Buy More Votes Button - Top Right */}
          {!showBuyVotes && (
            <Button
              onClick={() => setShowBuyVotes(true)}
              className="bg-gray-500 hover:bg-gray-600 text-white rounded-full"
              data-testid="buy-votes-btn"
            >
              <span className="mr-2 font-bold">R</span>
              Buy More Votes
            </Button>
          )}
        </div>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-2 tracking-tight">Vote for Artists</h1>
          <p className="text-muted-foreground">
            {currentUser?.is_admin ? (
              <>You have <span className="text-secondary font-bold">Unlimited</span> votes (Admin)</>
            ) : (
              <>You have <span className="text-secondary font-bold">{currentUser?.free_votes_remaining || 0}</span> votes remaining</>
            )}
          </p>
        </div>

        {showBuyVotes && (
          <Card className="glass-panel p-8 mb-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Buy More Votes</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowBuyVotes(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium mb-2 block">Select Amount (R1 = 1 Vote)</label>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                  {voteOptions.map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setBuyAmount(amount)}
                      className={`p-4 rounded-xl border-2 transition-colors ${
                        buyAmount === amount
                          ? 'border-secondary bg-secondary/20'
                          : 'border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="font-bold">R{amount}</div>
                      <div className="text-sm text-muted-foreground">{amount} votes</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Upload Proof of Payment</label>
                <div className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center">
                  <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setProofFile(e.target.files[0])}
                    className="hidden"
                    id="proof-upload"
                  />
                  <label 
                    htmlFor="proof-upload" 
                    className="cursor-pointer inline-block px-6 py-3 border border-white/20 rounded-full hover:bg-white/10 transition-colors"
                  >
                    Select File
                  </label>
                  {proofFile && (
                    <p className="mt-4 text-sm text-secondary">{proofFile.name}</p>
                  )}
                </div>
              </div>

              <Button
                onClick={handleBuyVotes}
                className="w-full bg-secondary text-black hover:brightness-110 rounded-full"
                data-testid="submit-payment-btn"
              >
                Submit Payment (R{buyAmount})
              </Button>
            </div>
          </Card>
        )}

        {/* Search and View Toggle */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          {/* Search Field */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search artists by name or talent..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 focus:border-primary h-12 rounded-xl"
              data-testid="artist-search-input"
            />
          </div>
          
          {/* View Toggle Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={() => setViewMode('cards')}
              className={`rounded-xl px-4 ${viewMode === 'cards' ? 'bg-orange-500 text-white' : 'bg-white/10 hover:bg-white/20'}`}
              data-testid="view-cards-btn"
            >
              <LayoutGrid className="h-5 w-5 mr-2" />
              Cards
            </Button>
            <Button
              onClick={() => setViewMode('list')}
              className={`rounded-xl px-4 ${viewMode === 'list' ? 'bg-orange-500 text-white' : 'bg-white/10 hover:bg-white/20'}`}
              data-testid="view-list-btn"
            >
              <List className="h-5 w-5 mr-2" />
              List
            </Button>
          </div>
        </div>

        {/* Results count */}
        {searchQuery && (
          <p className="text-sm text-muted-foreground mb-4">
            Found {filteredArtists.length} artist{filteredArtists.length !== 1 ? 's' : ''} matching "{searchQuery}"
          </p>
        )}

        {/* Cards View */}
        {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading ? (
            <div className="col-span-full flex flex-col items-center justify-center py-16">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground text-lg">Loading....</p>
            </div>
          ) : filteredArtists.length === 0 ? (
            <Card className="glass-panel p-12 text-center col-span-full">
              <p className="text-muted-foreground">
                {searchQuery ? `No artists found matching "${searchQuery}"` : 'No artists registered yet'}
              </p>
            </Card>
          ) : (
            filteredArtists.map((artist) => (
              <Card
                key={artist.user_id}
                className="glass-panel p-6 hover:border-primary/50 transition-colors"
                data-testid={`artist-card-${artist.user_id}`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-primary/20 mb-4">
                    {artist.picture ? (
                      <img src={artist.picture} alt={artist.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="h-12 w-12 text-primary" />
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-xl font-bold mb-2">{artist.stage_name || artist.name}</h3>
                  
                  {artist.talents && artist.talents.length > 0 && (
                    <div className="flex flex-wrap gap-2 justify-center mb-4">
                      {artist.talents.slice(0, 2).map((talent, idx) => (
                        <span key={idx} className="px-2 py-1 bg-primary/20 rounded-full text-xs">
                          {talent}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="w-full space-y-3 mt-4">
                    <div className="text-center py-2 px-4 bg-white/5 rounded-xl">
                      <p className="text-xs text-muted-foreground">Votes Received</p>
                      <p className="text-2xl font-bold text-secondary">{leaderboard[artist.user_id] || 0}</p>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleVote(artist.user_id)}
                        className="flex-1 bg-primary rounded-full hover:scale-105 transition-transform"
                        data-testid={`vote-btn-${artist.user_id}`}
                      >
                        <Vote className="mr-2 h-4 w-4" />
                        Vote
                      </Button>
                      
                      {userVotes[artist.user_id] > 0 && (
                        <Button
                          onClick={() => handleUnvote(artist.user_id)}
                          variant="outline"
                          className="rounded-full border-red-500 text-red-500 hover:bg-red-500/10"
                          data-testid={`unvote-btn-${artist.user_id}`}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    {userVotes[artist.user_id] > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Your votes: {userVotes[artist.user_id]}
                      </p>
                    )}

                    <Button
                      variant="outline"
                      onClick={() => navigate(`/profile/${artist.user_id}`)}
                      className="w-full rounded-full"
                    >
                      View Profile
                    </Button>

                    {/* Admin Vote Section */}
                    {currentUser?.is_admin && (
                      <div className="pt-4 border-t border-white/10">
                        {showAdminVote === artist.user_id ? (
                          <div className="space-y-3 bg-purple-900/30 p-4 rounded-xl">
                            <h4 className="font-bold text-sm flex items-center gap-2">
                              <Shield className="h-4 w-4 text-purple-400" />
                              Admin Vote
                            </h4>
                            
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => setAdminVoteAction('increase')}
                                className={`flex-1 rounded-full ${adminVoteAction === 'increase' ? 'bg-green-600' : 'bg-gray-600'}`}
                              >
                                <TrendingUp className="h-4 w-4 mr-1" />
                                Increase
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => setAdminVoteAction('decrease')}
                                className={`flex-1 rounded-full ${adminVoteAction === 'decrease' ? 'bg-red-600' : 'bg-gray-600'}`}
                              >
                                <TrendingDown className="h-4 w-4 mr-1" />
                                Decrease
                              </Button>
                            </div>
                            
                            <div>
                              <label className="text-xs text-muted-foreground">Number of Votes</label>
                              <Input
                                type="number"
                                min="1"
                                value={adminVoteCount}
                                onChange={(e) => setAdminVoteCount(parseInt(e.target.value) || 0)}
                                className="bg-white/10 mt-1"
                              />
                            </div>
                            
                            <div>
                              <label className="text-xs text-muted-foreground">Reason (shown on profile)</label>
                              <Textarea
                                value={adminVoteReason}
                                onChange={(e) => setAdminVoteReason(e.target.value)}
                                placeholder="Enter reason for vote adjustment..."
                                className="bg-white/10 mt-1 min-h-[60px]"
                              />
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                onClick={() => handleAdminVoteSubmit(artist.user_id)}
                                disabled={isSubmittingAdminVote}
                                className={`flex-1 rounded-full ${adminVoteAction === 'increase' ? 'bg-green-600' : 'bg-red-600'}`}
                              >
                                {isSubmittingAdminVote ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  `Submit ${adminVoteAction === 'increase' ? '+' : '-'}${adminVoteCount}`
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setShowAdminVote(null);
                                  setAdminVoteReason('');
                                }}
                                className="rounded-full"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            onClick={() => setShowAdminVote(artist.user_id)}
                            className="w-full bg-purple-600 hover:bg-purple-700 rounded-full"
                            data-testid={`admin-vote-btn-${artist.user_id}`}
                          >
                            <Shield className="mr-2 h-4 w-4" />
                            Admin Vote
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="space-y-3">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground text-lg">Loading....</p>
              </div>
            ) : filteredArtists.length === 0 ? (
              <Card className="glass-panel p-12 text-center">
                <p className="text-muted-foreground">
                  {searchQuery ? `No artists found matching "${searchQuery}"` : 'No artists registered yet'}
                </p>
              </Card>
            ) : (
              filteredArtists.map((artist) => (
                <Card
                  key={artist.user_id}
                  className="glass-panel p-4 hover:border-primary/50 transition-colors"
                  data-testid={`artist-list-${artist.user_id}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Profile Picture */}
                    <div className="w-14 h-14 rounded-full overflow-hidden bg-primary/20 flex-shrink-0">
                      {artist.picture ? (
                        <img src={artist.picture} alt={artist.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="h-7 w-7 text-primary" />
                        </div>
                      )}
                    </div>

                    {/* Artist Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg truncate">{artist.stage_name || artist.name}</h3>
                      {artist.talents && artist.talents.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {artist.talents.slice(0, 2).map((talent, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-primary/20 rounded-full text-xs">
                              {talent}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Votes */}
                    <div className="text-center px-4">
                      <p className="text-2xl font-bold text-secondary">{leaderboard[artist.user_id] || 0}</p>
                      <p className="text-xs text-muted-foreground">votes</p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <Button
                        onClick={() => handleVote(artist.user_id)}
                        className="bg-primary rounded-full hover:scale-105 transition-transform"
                        size="sm"
                        data-testid={`vote-list-btn-${artist.user_id}`}
                      >
                        <Vote className="h-4 w-4 mr-1" />
                        Vote
                      </Button>
                      
                      {userVotes[artist.user_id] > 0 && (
                        <Button
                          onClick={() => handleUnvote(artist.user_id)}
                          variant="outline"
                          size="sm"
                          className="rounded-full border-red-500 text-red-500 hover:bg-red-500/10"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/profile/${artist.user_id}`)}
                        className="rounded-full hidden md:flex"
                      >
                        Profile
                      </Button>

                      {currentUser?.is_admin && (
                        <Button
                          onClick={() => setShowAdminVote(artist.user_id)}
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 rounded-full"
                        >
                          <Shield className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Admin Vote Panel (expanded) */}
                  {currentUser?.is_admin && showAdminVote === artist.user_id && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="space-y-3 bg-purple-900/30 p-4 rounded-xl">
                        <h4 className="font-bold text-sm flex items-center gap-2">
                          <Shield className="h-4 w-4 text-purple-400" />
                          Admin Vote for {artist.stage_name || artist.name}
                        </h4>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => setAdminVoteAction('increase')}
                            className={`flex-1 rounded-full ${adminVoteAction === 'increase' ? 'bg-green-600' : 'bg-gray-600'}`}
                          >
                            <TrendingUp className="h-4 w-4 mr-1" />
                            Increase
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setAdminVoteAction('decrease')}
                            className={`flex-1 rounded-full ${adminVoteAction === 'decrease' ? 'bg-red-600' : 'bg-gray-600'}`}
                          >
                            <TrendingDown className="h-4 w-4 mr-1" />
                            Decrease
                          </Button>
                        </div>

                        <Input
                          type="number"
                          value={adminVoteCount}
                          onChange={(e) => setAdminVoteCount(parseInt(e.target.value) || 0)}
                          placeholder="Vote count"
                          min={1}
                          className="bg-white/10 border-white/20"
                        />

                        <Textarea
                          value={adminVoteReason}
                          onChange={(e) => setAdminVoteReason(e.target.value)}
                          placeholder="Reason for vote adjustment (required)"
                          className="bg-white/10 border-white/20"
                          rows={2}
                        />

                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleAdminVoteSubmit(artist.user_id)}
                            disabled={isSubmittingAdminVote}
                            className={`flex-1 rounded-full ${adminVoteAction === 'increase' ? 'bg-green-600' : 'bg-red-600'}`}
                          >
                            {isSubmittingAdminVote ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              `Submit ${adminVoteAction === 'increase' ? '+' : '-'}${adminVoteCount}`
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setShowAdminVote(null);
                              setAdminVoteReason('');
                            }}
                            className="rounded-full"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default VotingPage;
